import { CSSProperties } from 'react';
import { Clock } from '../atoms/Icons';
import { Caveat } from '../atoms/Caveat';
import { useT } from '../../../i18n/useT';
import { P, S, T, G } from '../../../styles/tokens';

interface TerminalSheetProps {
  variant: 'take' | 'done';
  cashbackAmount?: number;
  holdbackText?: string;
  onCta?: () => void;
}

// S01 — slot-taken confirmation sheet (design ref: task-flow.jsx TF_Confirm)
function TakeSheet({ cashbackAmount, onCta }: Pick<TerminalSheetProps, 'cashbackAmount' | 'onCta'>) {
  const takenTitle = useT('flow.terminal.taken');

  const overlayStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: S.overlayDim,
    zIndex: 39,
  };

  const sheetStyle: CSSProperties = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 40,
    background: P.bg,
    borderRadius: '24px 24px 0 0',
    padding: '12px 20px 38px',
    boxShadow: '0 -10px 40px rgba(0,0,0,0.18)',
  };

  const grabHandleStyle: CSSProperties = {
    width: 36,
    height: 5,
    background: S.tertiaryLabel,
    borderRadius: 3,
    margin: '0 auto 18px',
  };

  const iconWrapStyle: CSSProperties = {
    width: 84,
    height: 84,
    borderRadius: 42,
    background: P.terraSoft,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 14px',
    boxShadow: 'inset 0 0 0 8px rgba(226,114,91,0.18)',
  };

  const deadlineBoxStyle: CSSProperties = {
    ...G.warmTag,
    borderRadius: 14,
    padding: 14,
    marginTop: 18,
  };

  const ctaStyle: CSSProperties = {
    marginTop: 16,
    height: 50,
    borderRadius: 25,
    background: P.terracotta,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    font: T.headline,
    fontWeight: 600,
    boxShadow: '0 6px 16px rgba(192,81,58,0.25)',
    border: 'none',
    width: '100%',
    cursor: 'pointer',
  };

  return (
    <>
      <div style={overlayStyle} />
      <div style={sheetStyle}>
        <div style={grabHandleStyle} />
        {/* Shopping bag icon — replaced emoji with SVG to avoid 1999-HTML tell */}
        <div style={iconWrapStyle}>
          <svg width={38} height={38} viewBox="0 0 38 38" fill="none" aria-hidden="true">
            <path
              d="M10 16h18l-2 12H12L10 16z"
              stroke={P.terraDark}
              strokeWidth="1.8"
              strokeLinejoin="round"
              fill={P.terraSoft}
            />
            <path
              d="M14 16v-3a5 5 0 0110 0v3"
              stroke={P.terraDark}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <circle cx="15.5" cy="22" r="1.2" fill={P.terraDark} />
            <circle cx="22.5" cy="22" r="1.2" fill={P.terraDark} />
          </svg>
        </div>
        <div style={{ font: T.title2, color: S.label, textAlign: 'center', lineHeight: 1.2 }}>
          {takenTitle}
        </div>
        {cashbackAmount !== undefined && (
          <div style={{ font: T.subhead, color: S.secondaryLabel, textAlign: 'center', marginTop: 6, lineHeight: 1.5 }}>
            вернётся{' '}
            <b style={{ color: P.terraDark }}>+{cashbackAmount.toLocaleString('ru-RU')} ₽</b>
          </div>
        )}
        <div style={deadlineBoxStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: T.footnote, fontWeight: 600, color: P.warn }}>
            <span style={{ color: P.warn, display: 'flex', alignItems: 'center' }}>
              <Clock size={14} />
            </span>
            30 минут на оформление заказа
          </div>
          <div style={{ font: T.caption, color: S.secondaryLabel, marginTop: 6, lineHeight: 1.5 }}>
            Не успеешь — слот сгорит, рейтинг снизится. После заказа таймер сменится: 24-48 часов на каждый шаг.
          </div>
        </div>
        {onCta && (
          <button onClick={onCta} style={ctaStyle}>
            К шагу 1
          </button>
        )}
      </div>
    </>
  );
}

// Done variant — matches prototype TF_Done: radial gradient, celebratory circle,
// Caveat annotations, serif hero amount, white glass card with holdback text.
function DoneSheet({ cashbackAmount, holdbackText, onCta }: Omit<TerminalSheetProps, 'variant'>) {
  const doneTitle = useT('flow.terminal.done.title');

  const wrapStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: `radial-gradient(circle at 50% 30%, ${P.terraSoft} 0%, ${P.bg} 60%)`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    textAlign: 'center',
  };

  const circleBgStyle: CSSProperties = {
    width: 120,
    height: 120,
    borderRadius: 60,
    background: P.terracotta,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 20px 40px rgba(226,114,91,0.35)',
    position: 'relative',
    marginBottom: 24,
  };

  return (
    <div style={wrapStyle}>
      <div style={{ position: 'relative', marginBottom: 24 }}>
        {/* Terracotta celebration circle with SVG star burst */}
        <div style={circleBgStyle}>
          <svg width={54} height={54} viewBox="0 0 54 54" fill="none" aria-hidden="true">
            {/* confetti burst */}
            <circle cx="27" cy="27" r="16" fill="rgba(255,255,255,0.25)" />
            <path d="M27 11v4M27 39v4M11 27h4M39 27h4M16 16l3 3M38 35l3 3M35 16l3-3M16 38l3-3"
              stroke="rgba(255,255,255,0.70)" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M20 27a7 7 0 1014 0 7 7 0 00-14 0z" fill="rgba(255,255,255,0.90)" />
            <path d="M24 27l2.5 2.5L30 24" stroke={P.terracotta} strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {/* Caveat accent annotations */}
        <div style={{ position: 'absolute', top: -6, left: -36 }}>
          <Caveat color={P.jade} size={26} rot={-12}>ура!</Caveat>
        </div>
        {cashbackAmount !== undefined && (
          <div style={{ position: 'absolute', bottom: 4, right: -34 }}>
            <Caveat color={P.terraDark} size={22} rot={14}>
              +{cashbackAmount.toLocaleString('ru-RU')}₽
            </Caveat>
          </div>
        )}
      </div>

      {/* Title */}
      <div style={{ font: T.title2, color: S.label, lineHeight: 1.15 }}>
        {doneTitle}
      </div>
      <div style={{ font: T.subhead, color: S.secondaryLabel, marginTop: 8, lineHeight: 1.5, maxWidth: 280 }}>
        Все шаги приняты
      </div>

      {/* Glass card — cashback details */}
      {cashbackAmount !== undefined && (
        <div style={{
          marginTop: 24,
          padding: 16,
          background: P.card,
          borderRadius: 18,
          border: `1px solid ${P.line}`,
          width: '100%',
          maxWidth: 300,
          textAlign: 'left',
        }}>
          <div style={{
            font: T.caption,
            color: S.secondaryLabel,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            Зачислено в копилку
          </div>
          <div style={{
            fontFamily: T.fontDisplay,
            fontSize: 30,
            fontWeight: 700,
            color: P.success,
            marginTop: 4,
            lineHeight: 1.0,
          }}>
            +{cashbackAmount.toLocaleString('ru-RU')} ₽
          </div>
          <div style={{ font: T.caption, color: S.secondaryLabel, marginTop: 6, lineHeight: 1.4 }}>
            Будет на балансе{' '}
            <b style={{ color: S.label }}>через 14 дней</b>
            <br />
            {holdbackText && (
              <span style={{ color: P.muted }}>{holdbackText}</span>
            )}
            {!holdbackText && (
              <span style={{ color: P.muted }}>(стандартная выдержка)</span>
            )}
          </div>
        </div>
      )}

      {onCta && (
        <button
          onClick={onCta}
          style={{
            marginTop: 28,
            height: 50,
            borderRadius: 25,
            background: P.terracotta,
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            padding: '0 32px',
            font: T.headline,
            fontWeight: 600,
            boxShadow: '0 6px 16px rgba(192,81,58,0.25)',
          }}
        >
          К находкам
        </button>
      )}
    </div>
  );
}

export function TerminalSheet({ variant, cashbackAmount, holdbackText, onCta }: TerminalSheetProps) {
  if (variant === 'take') {
    return <TakeSheet cashbackAmount={cashbackAmount} onCta={onCta} />;
  }

  return (
    <DoneSheet
      cashbackAmount={cashbackAmount}
      holdbackText={holdbackText}
      onCta={onCta}
    />
  );
}
